import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CompanyContactService, LLFormService } from '../services/index';
import { ILLBusinessCategory } from '../models';
import { ILLProductDashboard } from '../models/ll-product-dashboard';

@Injectable()
export class ProductDashboardResolverService implements Resolve<any> {
  revisionId: number | undefined;

  constructor(private companyService: CompanyContactService,
              private llService: LLFormService) {
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    if (!isNaN(route.params.revisionId)) {
      this.revisionId = parseInt(route.params.revisionId, 10);
    }

    const retVal = forkJoin([
        this.revisionId ? this.llService.getLLFormById(this.revisionId.toString()) : of(null),
        this.companyService.getBusinessCategories(false),
        this.revisionId ? this.llService.getRevisionHistory(this.revisionId) : of(null)]
    )
    .pipe(map(([llResult, businessCategories, revisionHistory]: [any, ILLBusinessCategory[], any]) => [
      this.mapProductDashboardHeader(llResult?.data ?? llResult, businessCategories),
      businessCategories,
      revisionHistory
    ]))
    .pipe(catchError(err => of({ error: err })));

    return retVal;
  }

  private mapProductDashboardHeader(llData: any, businessCategories: ILLBusinessCategory[] = []): ILLProductDashboard {
    if (!llData) {
      return null;
    }

    const products = Array.isArray(llData.products) ? llData.products : [];
    const product = this.getProductForHeader(products);
    const businessCategory = llData.businessCategory
      || businessCategories.find(category => `${category.categoryId}` === `${llData.businessCategoryId}`)
      || { categoryId: '', label: '', description: '', activeFlag: 'A' };
    const versionNumber = this.getVersionNumber(llData);

    return {
      llId: Number(llData.llId ?? 0),
      llRevisionId: Number(llData.llRevisionId ?? 0),
      isDraftMode: !!llData.isDraftMode,
      formName: llData.formName ?? '',
      proprietaryName: product?.proprietaryName ?? llData.proprietaryName ?? '',
      modifiedBy: llData.modifiedByUser ?? llData.createUser ?? '',
      modifiedDate: llData.modifiedByDate ?? llData.createDate,
      ndcPackageCodes: products.map(x => x?.ndcProductCode).filter(Boolean).join(', ') || llData.ndcProductCode || '',
      setId: llData.setId ?? '',
      businessCategory,
      labelerName: this.getLabelerName(llData.labelerCompany),
      productType: llData.serviceType?.label ?? llData.splServiceType?.label ?? product?.productType ?? '',
      formVersion: llData.formatedVersion ?? (llData.isDraftMode && versionNumber ? `${versionNumber} (Draft)` : versionNumber),
      certificationExpirationDate: product?.listingRecordCertifiedThroughDate,
      productOrders: []
    };
  }

  private getProductForHeader(products: any[]): any {
    if (!products.length) {
      return null;
    }

    return products
      .slice()
      .sort((first, second) => this.getDateTime(first?.listingRecordCertifiedThroughDate) - this.getDateTime(second?.listingRecordCertifiedThroughDate))[0];
  }

  private getDateTime(value: any): number {
    const time = value ? new Date(value).getTime() : Number.MAX_SAFE_INTEGER;
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
  }

  private getLabelerName(labelerCompany: any): string {
    if (!labelerCompany?.companyName) {
      return '';
    }

    return labelerCompany.dunsNumber
      ? `${labelerCompany.companyName} (${labelerCompany.dunsNumber})`
      : labelerCompany.companyName;
  }

  private getVersionNumber(llData: any): string {
    if (llData.formatedVersion) {
      return llData.formatedVersion;
    }

    if (llData.majorVersion === undefined || llData.majorVersion === null) {
      return llData.splVersion ?? '';
    }

    return `${llData.majorVersion}.${llData.minorVersion ?? llData.minorRevision ?? 0}`;
  }
}
